import {
  AudienceIcon,
  Column,
  FormField,
  IconBox,
  Row,
  Select,
  Text,
} from "@hightouch/htui";
import { useEffect } from "react";
import { produce } from "immer";
import { TraitParentModel } from "src/components/audiences/types";
import { Controller, useFormContext } from "react-hook-form";
import { ReadOnlyConditions } from "src/components/explore/read-only-conditions";
import { QueryBuilder } from "src/components/explore";
import { PREDICTIVE_ELIGIBILITY_RESTRICTIONS } from "src/components/traits/predictive-utils";
import { predictionUpdateFrequencyOptions } from "./predictive-trait-calculation";
import { useEligibleTargetAudiencesQuery } from "src/graphql";

type Props = {
  parentModel: TraitParentModel;
};

export function LookalikeAudienceTraitCalculation({ parentModel }: Props) {
  const features = parentModel.predictor_feature_definitions;
  const { setValue, getValues } = useFormContext();

  useEffect(() => {
    if (features && features.length > 0) {
      const current = getValues("predictorDefinition.featureDefinitionId");
      if (!current) {
        setValue(
          "predictorDefinition.featureDefinitionId",
          features[0]?.id ?? "",
        );
      }
    }
  }, [features, setValue, getValues]);

  const { data: audiencesData, isLoading: isLoadingAudiences } =
    useEligibleTargetAudiencesQuery({
      parentModelId: parentModel.id,
    });

  const audienceOptions =
    audiencesData?.segments.map((s) => ({
      label: s.name,
      value: s.id.toString(),
    })) ?? [];

  if (features && features.length === 0) {
    return (
      <Column>
        <Text>
          No feature definitions found for this parent model. Please create one
          from the Schema page's Predictive Inputs tab first.
        </Text>
      </Column>
    );
  }

  return (
    <Column gap={6}>
      <Controller
        name="predictorDefinition.baseAudienceId"
        render={({ field }) => (
          <FormField
            label="Which audience do you want to mimic?"
            description="Lookalike audiences are based on the characteristics of a selected audience."
          >
            <Select
              isLoading={isLoadingAudiences}
              placeholder="Select an audience"
              options={audienceOptions}
              value={field.value?.toString()}
              onChange={field.onChange}
            />
          </FormField>
        )}
      />
      <Controller
        name="predictorDefinition.audienceDefinition"
        render={({ field }) => (
          <FormField
            label="Add filters"
            description="Include more filters to restrict the match pool to specific users."
            isOptional
          >
            <Column
              p={4}
              borderWidth="hairline"
              borderStyle="solid"
              borderColor="base.border"
              borderRadius="md"
            >
              <QueryBuilder
                maxNestingDepth={0}
                filter={field.value}
                updateConditions={(updater) =>
                  field.onChange(produce(field.value, updater))
                }
                parentModel={parentModel}
                enableNestedFiltering={false}
                restrictions={PREDICTIVE_ELIGIBILITY_RESTRICTIONS}
              />
            </Column>
          </FormField>
        )}
      />
      <Controller
        name="predictorDefinition.config.inferenceCadence"
        render={({ field }) => (
          <FormField
            label="How often should scores be updated?"
            description="Set how frequently lookalike scores refresh using the latest data. Faster updates improve freshness but use more resources."
          >
            <Select
              options={predictionUpdateFrequencyOptions}
              value={field.value ?? "weekly"}
              onChange={field.onChange}
            />
          </FormField>
        )}
      />
    </Column>
  );
}

type ReadOnlyProps = {
  parentModel?: Pick<TraitParentModel, "id" | "name" | "relationships"> & {
    connection: { id: string } | null;
  };
  baseAudienceId?: string | number | null;
  audienceDefinition?: any;
  inferenceCadence?: string;
};

export function LookalikeAudienceTraitCalculationReadOnly({
  parentModel,
  baseAudienceId,
  audienceDefinition,
  inferenceCadence,
}: ReadOnlyProps) {
  const { data: audiencesData } = useEligibleTargetAudiencesQuery(
    { parentModelId: parentModel?.id ?? "" },
    { enabled: Boolean(parentModel) },
  );

  const selectedAudience = audiencesData?.segments.find(
    (s) => s.id.toString() === baseAudienceId?.toString(),
  );

  return (
    <Column gap={6} bg="base.background" borderRadius="md" py={3} px={4}>
      <Column gap={2}>
        <Text fontWeight="medium">Source audience</Text>
        <Row gap={1} alignItems="center">
          <IconBox icon={<AudienceIcon />} color="blue" size="xs" />
          <Text>{selectedAudience?.name ?? "Unknown audience"}</Text>
        </Row>
      </Column>
      <Column gap={2}>
        <Text fontWeight="medium">Match pool</Text>
        {audienceDefinition?.conditions &&
        audienceDefinition?.conditions.length > 0 ? (
          <ReadOnlyConditions
            rootConditions={[audienceDefinition]}
            audiences={[]}
            traits={[]}
            relationships={parentModel?.relationships || []}
            journeys={[]}
          />
        ) : (
          <Text>No filters</Text>
        )}
      </Column>
      <Column gap={2}>
        <Text fontWeight="medium">How often should scores be updated?</Text>
        <Text>
          {
            predictionUpdateFrequencyOptions.find(
              (option) => option.value === (inferenceCadence ?? "weekly"),
            )?.label
          }
        </Text>
      </Column>
    </Column>
  );
}
